import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { WARootState } from '@store/store';

interface ImpersonationState {
  isImpersonating: boolean;
  /**
   * Impersonating, excluding team-members-only advisors. Legacy treats those
   * sessions as normal for Log Out and the impersonation banner / exit control.
   * Feature guards should keep using `isImpersonating`.
   */
  isRestrictedImpersonation: boolean;
  /** The banner and "Exit user" control are shown; always equals `isRestrictedImpersonation`. */
  requiresExitUser: boolean;
  isImpersonatedByAdministrator: boolean;
  roleImpersonatedBy: string | undefined;
  userName: string | undefined;
  isInIframe: boolean;
}

export function useImpersonation(): ImpersonationState {
  const isImpersonating = useSelector(
    (state: WARootState) => state.appView.isImpersonation,
  );
  // Sticky slice flag, not UserInfo: UserInfo is undefined while a refetch is
  // pending or after it fails, which would wrongly mark these sessions restricted.
  const isTeamMembersOnlyAdvisor = useSelector(
    (state: WARootState) => state.appView.isTeamMembersOnlyAdvisor,
  );
  const isImpersonatedByAdministrator = useSelector(
    (state: WARootState) => state.appView.UserInfo?.IsAdmin === true,
  );
  const roleImpersonatedBy = useSelector(
    (state: WARootState) => state.appView.UserInfo?.RoleImpersonatedBy,
  );
  const userName = useSelector(
    (state: WARootState) => state.appView.UserInfo?.Name,
  );

  const isInIframe = useMemo(
    () => typeof window !== 'undefined' && window.parent !== window,
    [],
  );

  const isRestrictedImpersonation =
    isImpersonating && !isTeamMembersOnlyAdvisor;

  return {
    isImpersonating,
    isRestrictedImpersonation,
    requiresExitUser: isRestrictedImpersonation,
    isImpersonatedByAdministrator,
    roleImpersonatedBy,
    userName,
    isInIframe,
  };
}
